import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { PageHeader, StatCard, rupee } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Download, Printer, ChevronLeft, Wallet, Clock, CheckCircle2 } from "lucide-react";
import { ReportDateFilter, useReportRange } from "../components/ReportDateFilter";

const prettyDate = (s) => { if (!s) return "—"; const [y, m, d] = s.split("-"); return new Date(y, m - 1, d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); };

function downloadCsv(name, head, rows) {
  const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c ?? "")}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `${name}.csv`; a.click();
}

export default function AdvanceReport({ report, onBack }) {
  const { branchParam, selected, branches } = useBranch();
  const branchLabel = (selected && selected !== "all") ? (branches.find((b) => b.id === selected)?.name || "Your Branch") : "All Branches";
  const [status, setStatus] = useState("all");
  const rr = useReportRange("month");
  const { start, end } = rr;
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    const params = { branch_id: branchParam, start, end };
    if (status !== "all") params.status = status;
    api.get("/staff-advances/report", { params }).then((r) => setData(r.data));
  }, [branchParam, status, start, end]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const totals = data?.totals || { total: 0, pending: 0, deducted: 0, count: 0 };

  const csvExport = () => downloadCsv("staff_advance_report",
    ["S.No", "Staff", "Branch", "Advance Date", "Amount", "Pending", "Deducted", "Deduction Month", "Status"],
    rows.map((r, i) => [i + 1, r.staff_name, r.branch_name, r.date, r.amount, r.pending_amount, r.deducted_amount, r.deducted_month, r.status]));

  const pdfExport = async () => {
    try {
      const params = { branch_id: branchParam || undefined, start, end };
      if (status !== "all") params.status = status;
      const res = await api.get("/staff-advances/report.pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "staff_advance_report.pdf";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  return (
    <div>
      <button onClick={onBack} data-testid="advance-report-back" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3"><ChevronLeft size={16} /> All Reports</button>
      <PageHeader title="Staff Advance Report" subtitle="Outstanding & deducted staff advances — strictly branch-wise"
        actions={
          <div className="flex gap-2">
            <Button onClick={csvExport} variant="outline" className="rounded-sm" data-testid="advance-export-csv"><Download size={15} className="mr-1" /> Excel/CSV</Button>
            <Button onClick={pdfExport} variant="outline" className="rounded-sm" data-testid="advance-export-pdf"><Download size={15} className="mr-1" /> PDF</Button>
            <Button onClick={() => window.print()} variant="outline" className="rounded-sm" data-testid="advance-print"><Printer size={15} className="mr-1" /> Print</Button>
          </div>
        } />

      <Card className="p-4 bg-card border-border mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant="outline" className="border-primary text-primary" data-testid="advance-branch-chip">Showing: {branchLabel}</Badge>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Status</span>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="bg-secondary w-40" data-testid="advance-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="deducted">Deducted</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <ReportDateFilter r={rr} prefix="advance" className="mt-3" />
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Advances" value={rupee(totals.total)} icon={Wallet} testid="advance-kpi-total" />
        <StatCard label="Pending (Outstanding)" value={rupee(totals.pending)} icon={Clock} testid="advance-kpi-pending" />
        <StatCard label="Deducted" value={rupee(totals.deducted)} icon={CheckCircle2} testid="advance-kpi-deducted" />
        <StatCard label="Entries" value={totals.count} icon={Wallet} testid="advance-kpi-count" />
      </div>

      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Staff</TableHead><TableHead>Branch</TableHead><TableHead>Advance Date</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Pending</TableHead>
              <TableHead className="text-right">Deducted</TableHead><TableHead>Deduction Month</TableHead><TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} className="border-border" data-testid={`advance-row-${r.id}`}>
                <TableCell className="font-medium">{r.staff_name}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{r.branch_name}</TableCell>
                <TableCell>{prettyDate(r.date)}</TableCell>
                <TableCell className="text-right text-primary">{rupee(r.amount)}</TableCell>
                <TableCell className="text-right text-amber-400">{r.pending_amount ? rupee(r.pending_amount) : "—"}</TableCell>
                <TableCell className="text-right text-green-400">{r.deducted_amount ? rupee(r.deducted_amount) : "—"}</TableCell>
                <TableCell className="text-muted-foreground">{r.deducted_month}</TableCell>
                <TableCell>
                  {r.status === "deducted"
                    ? <Badge variant="outline" className="bg-green-900/40 text-green-300 border-green-800">Deducted</Badge>
                    : <Badge variant="outline" className="bg-amber-900/40 text-amber-300 border-amber-800">Pending</Badge>}
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No staff advances found.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
